import * as _ from 'lodash'
import * as toastr from 'toastr'

import * as UserManagement from '../'
import * as Common from '../../../Common/modules'
import * as Models from '../../../Models'
import * as Services from '../../../Services'
import * as UserManagementApiClient from './UserManagementApiClientService'

export interface IUserManagementApiClientService {
  getAllGroupSummaries(): ng.IPromise<Models.GroupModel.IGroupSummaryModel[]>;
  getGroupById(id: string): ng.IPromise<Models.GroupModel.IGroupModel>;
  getGroupTypes(): ng.IPromise<Models.GroupTypeModel.IGroupTypeModel[]>;
  getGroupsInPartner(partnerCode: string): ng.IPromise<Models.GroupModel.IGroupModel[]>;
  getAllRoles(includeGroups: boolean): ng.IPromise<Models.RoleModel.IRoleModel[]>;
  searchUsers(
    request: UserManagement.models.UserSearchRequest.IUserSearchRequest,
  ): ng.IPromise<UserManagement.models.UserSearchResult.IUserSearchResult[]>;
  searchRoles(roleName: string): ng.IPromise<Models.RoleModel.IRoleModel[]>;
  getUserById(id: string): angular.IPromise<Models.UserModel.IUserModel>;
  getUserByEmail(
    request: UserManagement.models.UserSearchRequest.IUserSearchRequest,
  ): angular.IPromise<Models.UserModel.IUserModel>;
  saveUser: (user: Models.UserModel.IUserModel) => ng.IPromise<void>;
  saveGroup: (user: Models.GroupModel.IGroupModel) => ng.IPromise<void>;
  saveRole: (user: Models.RoleModel.IRoleModel) => ng.IPromise<void>;
  lockUser: (userId: string) => ng.IPromise<void>;
  unlockUser: (userId: string) => ng.IPromise<void>;
  disableUser: (userId: string) => ng.IPromise<void>;
  enableUser: (userId: string) => ng.IPromise<void>;
  getUsersInGroup(groupId: string): ng.IPromise<UserManagement.models.UsernameResult.IUsernameResult[]>;
  getUsersInPartner(partnerCode: string): ng.IPromise<UserManagement.models.UserSearchResult.IUserSearchResult[]>;
  getUsernames(username: string): ng.IPromise<UserManagement.models.UsernameResult.IUsernameResult[]>;
  getRoleById(roleId: string): ng.IPromise<Models.RoleModel.IRoleModel>;
  deleteGroup(groupId: string): ng.IPromise<Models.BaseApiResponse.BaseApiResponse>;
  deleteRole(roleId: string): ng.IPromise<void>;
  createUser(
    createUserRequest: UserManagement.models.CreateUserRequest.ICreateUserRequest,
  ): ng.IPromise<Models.Response.Response>;
  getPartners(excludeUsersGroups?: boolean): ng.IPromise<Models.PartnerModel.IPartnerModel[]>;
  getPartnerByCode(partnerCode: string): ng.IPromise<Models.PartnerModel.IPartnerModel>;
  savePartner(partner: Models.PartnerModel.IPartnerModel): ng.IPromise<void>;
  getUsersInSalesGroup(partnerCode: string): ng.IPromise<UserManagement.models.UserSearchResult.IUserSearchResult[]>;
}

export class UserManagementApiClientService implements IUserManagementApiClientService {
  public routePrefix = "Admin";

  public static $inject = ["igs.apiClientService"];

  constructor(private igsApiClientService: Common.ApiClientService.Igs.Common.IApiClientService) {}

  public createUser = (
    createUserRequest: UserManagement.models.CreateUserRequest.ICreateUserRequest,
  ): ng.IPromise<Models.Response.Response> => {
    var url = `${this.routePrefix}/createuser`;
    return this.igsApiClientService.post(url, createUserRequest);
  };

  public getAllGroupSummaries(): angular.IPromise<Models.GroupModel.IGroupSummaryModel[]> {
    var url = `${this.routePrefix}/GroupSummaries`;
    var config = {};
    return this.igsApiClientService.get(url, config);
  }

  public getUsersInGroup = (
    groupId: string,
  ): angular.IPromise<UserManagement.models.UsernameResult.IUsernameResult[]> => {
    var url = `${this.routePrefix}/Group/${groupId}/Users`;
    var config = {};
    return this.igsApiClientService.get(url, config);
  };

  public getUsernames = (
    username: string,
  ): angular.IPromise<UserManagement.models.UsernameResult.IUsernameResult[]> => {
    var url = `${this.routePrefix}/Usernames/?username=${encodeURIComponent(username)}`;
    var config = { nomask: true };
    return this.igsApiClientService.get(url, config);
  };

  public getAllRoles(includeGroups: boolean): angular.IPromise<Models.RoleModel.IRoleModel[]> {
    var url = `${this.routePrefix}/Roles` + (includeGroups === false ? '?includeGroups=false' : '');
    var config = {};
    return this.igsApiClientService.get(url, config);
  }

  public getRoleById(id: string): angular.IPromise<Models.RoleModel.IRoleModel> {
    var url = `${this.routePrefix}/Role/${id}`;
    var config = {};
    return this.igsApiClientService.get(url, config);
  }

  public getGroupTypes(): angular.IPromise<Models.GroupTypeModel.IGroupTypeModel[]> {
      var url = `${this.routePrefix}/GroupTypes`;
      return this.igsApiClientService.get(url, {});
  }

  public searchUsers(
    request: UserManagement.models.UserSearchRequest.IUserSearchRequest,
  ): angular.IPromise<UserManagement.models.UserSearchResult.IUserSearchResult[]> {
    var url = `${this.routePrefix}/Users/Search`;
    return this.igsApiClientService.post(url, request);
  }

  public getGroupById(id: string): angular.IPromise<Models.GroupModel.IGroupModel> {
    var url = `${this.routePrefix}/Group/${id}`;
    var config = {};
    return this.igsApiClientService.get(url, config);
  }

  public searchRoles(roleName: string): angular.IPromise<Models.RoleModel.IRoleModel[]> {
    throw new Error("Not implemented");
  }

  public getUserById(id: string): angular.IPromise<Models.UserModel.IUserModel> {
    var url = `${this.routePrefix}/User/${id}`;
    var config = {};
    return this.igsApiClientService.get(url, config);
  }

  public getUserByEmail(
    request: UserManagement.models.UserSearchRequest.UserSearchRequest,
  ): angular.IPromise<Models.UserModel.IUserModel> {
    var url = `${this.routePrefix}/UserByEmail`;
    return this.igsApiClientService.post(url, request);
  }

  public saveUser = (user: Models.UserModel.IUserModel) => {
    var url = `${this.routePrefix}/User`;
    return this.igsApiClientService.post<void>(url, user);
  };

  public saveGroup = (group: Models.GroupModel.IGroupModel) => {
    var url = `${this.routePrefix}/Group`;
    return this.igsApiClientService.post<void>(url, group);
  };

  public deleteGroup = (groupId: string): angular.IPromise<Models.BaseApiResponse.BaseApiResponse> => {
    var url = `${this.routePrefix}/Group/${groupId}/Delete`;
    return this.igsApiClientService.post(url, null);
  };

  public deleteRole = (roleId: string) => {
    var url = `${this.routePrefix}/Role/${roleId}/Delete`;
    return this.igsApiClientService.post<void>(url, null);
  };

  public saveRole = (role: Models.RoleModel.IRoleModel) => {
    var url = `${this.routePrefix}/Role`;
    return this.igsApiClientService.post<void>(url, role);
  };

  public lockUser = (userId: string): ng.IPromise<void> => {
    var url = `${this.routePrefix}/User/${userId}/Lock`;
    return this.igsApiClientService.post<void>(url, null);
  };

  public unlockUser(userId: string): angular.IPromise<void> {
    var url = `${this.routePrefix}/User/${userId}/Unlock`;
    return this.igsApiClientService.post<void>(url, null);
  }

  public enableUser = (userId: string): ng.IPromise<void> => {
    var url = `${this.routePrefix}/User/${userId}/Enable`;
    return this.igsApiClientService.post<void>(url, null);
  };

  public disableUser(userId: string): angular.IPromise<void> {
    var url = `${this.routePrefix}/User/${userId}/Disable`;
    return this.igsApiClientService.post<void>(url, null);
  }

  // Partner Functions

  public getGroupsInPartner(partnerCode: string): angular.IPromise<Models.GroupModel.IGroupModel[]> {
    var url = `${this.routePrefix}/Partner/${partnerCode}/Groups`;
    return this.igsApiClientService.post(url, {partnerCode: partnerCode});
  }

  public getUsersInPartner = (
    partnerCode: string,
  ): angular.IPromise<UserManagement.models.UserSearchResult.IUserSearchResult[]> => {
    var url = `${this.routePrefix}/Partner/${partnerCode}/Users`;
    return this.igsApiClientService.get(url);
  };

  public getPartners(excludeUsersGroups?: boolean): angular.IPromise<Models.PartnerModel.IPartnerModel[]> {
    var url = `${this.routePrefix}/Partners` + (excludeUsersGroups ? '?excludeUsersGroups=1' : '');
    return this.igsApiClientService.get(url);
  }

  public getPartnerByCode(partnerCode: string): angular.IPromise<Models.PartnerModel.IPartnerModel> {
    var url = `${this.routePrefix}/Partner/${partnerCode}`;
    return this.igsApiClientService.get(url);
  }

  public savePartner(partner: Models.PartnerModel.IPartnerModel): angular.IPromise<void> {
    var url = `${this.routePrefix}/Partner`;
    return this.igsApiClientService.post(url, partner);
  }

  public getUsersInSalesGroup(
    partnerCode: string,
  ): ng.IPromise<UserManagement.models.UserSearchResult.IUserSearchResult[]> {
    var url = `${this.routePrefix}/Partner/${partnerCode}/SalesUsers`;
    return this.igsApiClientService.get(url);
  }
}
